import React from 'react'
import { ICellRendererParams } from 'ag-grid-enterprise'
import { Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../../types/company/Company'
import { SiblingDecision } from '../../utils/buildSiblingDecision'
import { alreadyMappedIdsFor, SiblingCandidates } from '../../utils/groupSuggestionsBySibling'
import SourceRecordName from '../SourceRecordName'
import CellSignalDot from './CellSignalDot'
import WizardGridCell from './WizardGridCell'

export interface WizardConfirmGridContext {
  targetId: string
  candidatesBySibling: Map<string, SiblingCandidates>
  decisions: Map<string, SiblingDecision>
}

interface DecisionCellRendererParams extends ICellRendererParams<Company, unknown, WizardConfirmGridContext> {
  role: 'customer' | 'supplier'
}

export const DecisionCellRenderer: React.FC<DecisionCellRendererParams> = ({ data, context, role }) => {
  const { t } = useTranslation()
  if (!data || !context) return null

  const sourceEntity = role === 'customer' ? 'Customer' : 'Supplier'
  const decision = context.decisions.get(data.id)
  const forwarded = role === 'customer' ? decision?.forwardCustomer : decision?.forwardSupplier
  if (forwarded?.length) {
    return (
      <WizardGridCell>
        <Space size={4} wrap>
          <CellSignalDot signal="suggestion" />
          {forwarded.map((sourceId, index) => (
            <span key={sourceId}>
              <SourceRecordName companyId={context.targetId} sourceEntity={sourceEntity} sourceId={sourceId} />
              {index < forwarded.length - 1 ? ',' : ''}
            </span>
          ))}
        </Space>
      </WizardGridCell>
    )
  }

  const candidateInfo = context.candidatesBySibling.get(data.id)
  const alreadyMappedIds = alreadyMappedIdsFor(role, candidateInfo)
  if (alreadyMappedIds.length) {
    return (
      <WizardGridCell>
        {alreadyMappedIds.map(sourceId => (
          <div key={sourceId}>
            <CellSignalDot signal="linked" />
            <SourceRecordName companyId={context.targetId} sourceEntity={sourceEntity} sourceId={sourceId} />
          </div>
        ))}
      </WizardGridCell>
    )
  }

  const remaining = role === 'customer' ? candidateInfo?.customer : candidateInfo?.supplier
  const hadCandidate = (remaining?.length ?? 0) > 0

  return (
    <WizardGridCell>
      <Typography.Text type="secondary">
        <CellSignalDot signal="none" />
        {hadCandidate
          ? t('groupCustomerMapping:wizardConfirmSkippedChoice', 'ohitettu — ei vastinetta')
          : t('groupCustomerMapping:wizardConfirmSkippedNoCandidate', 'ohitettu — ei ehdokasta')}
      </Typography.Text>
    </WizardGridCell>
  )
}
